import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "@/lib/session";
import { ArrowLeft, Building2, Crown, Users } from "lucide-react";
import { api } from "@/lib/api";

export default function DepartmentBattlesPage() {
  const navigate = useNavigate();
  const token = useToken();
  const [period, setPeriod] = useState("weekly");
  const [rows, setRows] = useState([]);
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/employee/department-leaderboard?token=${token}&period=${period}`).then((r) => setRows(r.data)),
      api.get(`/employee/department-of-the-week?token=${token}`).then((r) => setWinner(r.data)),
    ]).finally(() => setLoading(false));
  }, [token, period]);

  const top = rows[0];
  const maxScore = top?.score || 1;

  return (
    <div className="min-h-screen themed-bg">
      <header className="border-b-4 px-6 py-4 flex items-center justify-between themed-border" style={{ background: "var(--bq-header-bg)" }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="brutal-btn inline-flex items-center gap-1">
          <ArrowLeft size={14} /> HUB
        </button>
        <h1 className="font-black uppercase tracking-tight text-lg themed flex items-center gap-2">
          <Building2 size={20} /> Department Battles
        </h1>
        <div style={{ width: 60 }} />
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 grid gap-6">
        {winner?.department ? (
          <section data-testid="dotw-banner" className="brutal-card p-6 relative overflow-hidden" style={{ background: "var(--bq-accent)" }}>
            <div className="absolute -right-6 -top-6 text-8xl opacity-20 select-none">🏆</div>
            <div className="text-xs uppercase font-mono-bq tracking-widest flex items-center gap-1"><Crown size={14} /> Department of the Week</div>
            <div className="text-4xl md:text-5xl font-black uppercase mt-2">{winner.department}</div>
            <div className="text-sm mt-1 font-bold">{winner.score} combined points this week</div>
          </section>
        ) : (
          <section data-testid="dotw-empty" className="brutal-card p-6">
            <div className="text-xs uppercase font-mono-bq tracking-widest">Department of the Week</div>
            <div className="text-2xl font-black uppercase mt-1">Nobody yet — go score some points!</div>
          </section>
        )}

        <section className="brutal-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-black uppercase flex items-center gap-2"><Users size={18} /> Standings</h3>
            <div className="flex gap-2">
              {["daily", "weekly", "monthly"].map((p) => (
                <button
                  key={p}
                  data-testid={`dept-period-${p}`}
                  onClick={() => setPeriod(p)}
                  className={`brutal-btn text-xs ${period === p ? "themed-shadow-accent" : ""}`}
                >{p.toUpperCase()}</button>
              ))}
            </div>
          </div>
          {loading ? (
            <p className="text-sm" style={{ color: "var(--bq-muted)" }}>Loading…</p>
          ) : rows.length === 0 ? (
            <p data-testid="dept-empty" className="text-sm" style={{ color: "var(--bq-muted)" }}>
              No departments configured. Ask your admin to add a department on each employee record (via Admin → Users or CSV import).
            </p>
          ) : (
            <ol className="grid gap-3">
              {rows.map((row, i) => {
                const pct = Math.max(4, Math.round((row.score / maxScore) * 100));
                return (
                  <li key={row.department} data-testid={`dept-row-${i}`} className="border-2 themed-border p-3" style={{ background: "var(--bq-card-bg)" }}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 border-2 themed-border inline-flex items-center justify-center font-black">{i + 1}</div>
                        <div className="font-black uppercase">{row.department}</div>
                      </div>
                      <div className="font-mono-bq text-sm">
                        <span className="font-black">{row.score}</span> pts ·{" "}
                        <span>{row.active_players}/{row.players} active</span>
                      </div>
                    </div>
                    <div className="h-3 border-2 themed-border" style={{ background: "var(--bq-bg)" }}>
                      <div className="h-full" style={{ width: `${pct}%`, background: i === 0 ? "var(--bq-accent)" : "var(--bq-accent2)" }} />
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
}
